var __extends = (this && this.__extends) || (function () {
    var extendStatics = Object.setPrototypeOf ||
        ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
        function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
import { OutlineFilter } from "@pixi/filter-outline";
import { Container } from "pixi.js";
import { IdCursor } from "../../../events/IdCursor";
import { BF2AddBuildingCelebrationAnim, BF2GetBuildingDiscountCallout, } from "../../../graphics/BF2BuildingGraphicsUtils";
import { BF2GetBuildingCalloutsIso } from "../../../graphics/BF2GetBuildingCalloutsIso";
import { utilAnimation } from "../../../utils/animation/utilAnimation";
import { utilDrawGrid } from "../../../utils/graphics/utilDrawGrid";
import { BuildingStateType } from "../../../utils/network/generated/protoTypes";
import { shallowDiffers } from "../../../utils/shallowDiffers";
import { capitalize, pascalSentenceFromString } from "../../../utils/stringUtils";
import { utilBuildings } from "../../../utils/utilBuildings";
import { utilTick } from "../../../utils/utilTick";
import { utilTime } from "../../../utils/utilTime";
var Graphics = PIXI.Graphics;
var IsoBuilding = /** @class */ (function (_super) {
    __extends(IsoBuilding, _super);
    function IsoBuilding() {
        var _this = _super.call(this) || this;
        _this.displayCallouts = [];
        _this.productionStep = -1;
        _this.hasFinishedConstruction = false;
        _this.shouldIsoSort = true;
        _this.currentCalloutIndex = 0;
        _this.calloutTransitionMs = 7000;
        _this.dispose = function () {
            _this.tickSubscription$.unsubscribe();
        };
        _this.shouldUpdate = function (props, previousProps) {
            return (_this.shouldUpdateBuildingGraphic(props, previousProps) ||
                _this.shouldUpdateCalloutGraphic(props, previousProps) ||
                _this.hasBuildingDataChanged(props, previousProps));
        };
        _this.update = function (props) {
            if (!_this.shouldUpdate(props, _this.props)) {
                return false;
            }
            _this.productionStep = utilBuildings.getProductionCurrentStep(props.building);
            _this.updateGraphics(props);
            _this.updateGrid(props);
            _this.props = props;
            return true;
        };
        _this.requiresIsoSort = function () {
            return _this.shouldIsoSort;
        };
        _this.showHighlight = function () {
            if (_this.display != undefined) {
                var cache = _this.display.cacheAsBitmap;
                _this.display.cacheAsBitmap = false;
                _this.display.filters = [IsoBuilding.OutlineFilter];
                _this.display.cacheAsBitmap = cache;
            }
        };
        _this.hideHighlight = function () {
            if (_this.display != undefined) {
                var cache = _this.display.cacheAsBitmap;
                _this.display.cacheAsBitmap = false;
                // tslint:disable-next-line:no-null-keyword
                _this.display.filters = null;
                _this.display.cacheAsBitmap = cache;
            }
        };
        _this.showGrid = function () {
            if (_this.grid != undefined) {
                _this.addChildAt(_this.grid, 0);
            }
        };
        _this.hideGrid = function () {
            if (_this.grid != undefined && _this.grid.parent) {
                _this.removeChild(_this.grid);
            }
        };
        _this.onTick = function () {
            var currentStep = utilBuildings.getProductionCurrentStep(_this.props.building);
            var oldCalloutIndex = _this.currentCalloutIndex;
            var newCalloutIndex = _this.getCalloutIndex();
            if (oldCalloutIndex !== newCalloutIndex && _this.displayCallouts.length > oldCalloutIndex) {
                var oldCallout_1 = _this.displayCallouts[oldCalloutIndex];
                var newCallout = _this.displayCallouts[newCalloutIndex];
                _this.currentCalloutIndex = newCalloutIndex;
                _this.addChild(newCallout);
                utilAnimation.animateCrossfade(3, oldCallout_1, newCallout, function () {
                    return _this.removeChild(oldCallout_1);
                });
            }
            if (currentStep !== _this.productionStep) {
                _this.updateGraphics(_this.props);
                _this.productionStep = currentStep;
            }
        };
        _this.getCalloutIndex = function () {
            return _this.displayCallouts.length > 0
                ? Math.floor(utilTime.now() / _this.calloutTransitionMs) % _this.displayCallouts.length
                : 0;
        };
        _this.updateGrid = function (props) {
            if (_this.grid == undefined) {
                var _a = props.building, proto = _a.proto, balancing = _a.balancing;
                var sizeX = proto.flipped ? balancing.yExtent : balancing.xExtent;
                var sizeY = proto.flipped ? balancing.xExtent : balancing.yExtent;
                _this.grid = new Graphics();
                utilDrawGrid.draw(_this.grid, {
                    fillAlpha: 0.4,
                    fillColor: 0x000000,
                    sizeX: sizeX,
                    sizeY: sizeY,
                });
            }
            if (props.showGrid) {
                _this.showGrid();
            }
            else {
                _this.hideGrid();
            }
        };
        _this.hasBuildingDataChanged = function (props, previousProps) {
            if (!previousProps) {
                return true;
            }
            var finishedConstruction = utilBuildings.hasFinishedConstruction(props.building);
            var finishedConstructionChanged = finishedConstruction !== _this.hasFinishedConstruction;
            _this.hasFinishedConstruction = finishedConstruction;
            var protoChanged = shallowDiffers(props.building.proto, previousProps.building.proto);
            var productionChanged = shallowDiffers(props.building.production, previousProps.building.production);
            _this.shouldIsoSort = !(props.building.proto.posX === previousProps.building.proto.posX &&
                props.building.proto.posY === previousProps.building.proto.posY);
            return finishedConstructionChanged || protoChanged || productionChanged;
        };
        _this.shouldUpdateBuildingGraphic = function (props, previousProps) {
            return (!previousProps ||
                props.showGrid !== previousProps.showGrid ||
                props.animationEnabled !== previousProps.animationEnabled);
        };
        _this.shouldUpdateCalloutGraphic = function (props, previousProps) {
            return !previousProps || props.selected !== previousProps.selected;
        };
        _this.disposeGraphic = function (graphic) {
            if (!graphic) {
                return;
            }
            _this.removeChild(graphic);
            graphic.destroy();
        };
        _this.updateCalloutGraphic = function (props) {
            var building = props.building, selected = props.selected, isDiscounted = props.isDiscounted;
            _this.displayCallouts.forEach(function (callout) {
                _this.disposeGraphic(callout);
            });
            _this.displayCallouts = [];
            var isFinishedScaffolding = utilBuildings.hasFinishedConstruction(building);
            var displayCallout;
            if (isFinishedScaffolding) {
                displayCallout = BF2GetBuildingCalloutsIso(building, false);
            }
            else {
                displayCallout = BF2GetBuildingCalloutsIso(building, selected);
            }
            if (isDiscounted && !selected) {
                _this.displayCallouts.push(BF2GetBuildingDiscountCallout(building));
            }
            if (displayCallout != undefined) {
                _this.displayCallouts.push(displayCallout);
            }
            if (_this.displayCallouts.length > 0) {
                _this.currentCalloutIndex = _this.getCalloutIndex();
                var zIndex = Math.min(1, _this.children.length);
                _this.addChildAt(_this.displayCallouts[_this.currentCalloutIndex], zIndex);
            }
        };
        _this.updateBuildingGraphic = function (props) {
            var _a = props.building, proto = _a.proto, balancing = _a.balancing;
            var building = props.building, getBuildingGraphicIso = props.getBuildingGraphicIso, getBuildingScaffoldingIso = props.getBuildingScaffoldingIso, getBuildingFinishedScaffoldingIso = props.getBuildingFinishedScaffoldingIso, animationEnabled = props.animationEnabled;
            var isFinishedScaffolding = utilBuildings.hasFinishedConstruction(building);
            var isScaffolding = utilBuildings.isUnderConstruction(building);
            _this.preBuildingState = _this.buildingState;
            _this.buildingState = proto.state || BuildingStateType.IDLE;
            _this.disposeGraphic(_this.display);
            _this.display = undefined;
            _this.name = pascalSentenceFromString(balancing.name) + " [" + proto.stage + "][" + capitalize(BuildingStateType[proto.state || BuildingStateType.IDLE].toLowerCase()) + "]";
            if (animationEnabled &&
                utilBuildings.shouldPlayCelebrateAnimation(_this.buildingState, _this.preBuildingState)) {
                var buildingCenterPos = utilBuildings.getHoverPositionOnMap(building, 10);
                BF2AddBuildingCelebrationAnim(_this, buildingCenterPos);
            }
            if (isFinishedScaffolding) {
                _this.display = getBuildingFinishedScaffoldingIso(building);
            }
            else if (isScaffolding) {
                _this.display = getBuildingScaffoldingIso(building);
            }
            else {
                _this.display = getBuildingGraphicIso(building);
            }
            if (_this.display != undefined) {
                _this.display.interactive = true;
                _this.display.interactiveChildren = true;
                _this.display.name = proto.id + "|" + balancing.name;
                _this.addChildAt(_this.display, 0);
            }
        };
        _this.updateGraphics = function (props) {
            var hasBuildingDataChanged = _this.hasBuildingDataChanged(props, _this.props);
            var updateBuildingGraphic = _this.shouldUpdateBuildingGraphic(props, _this.props);
            var updateCalloutGraphic = _this.shouldUpdateCalloutGraphic(props, _this.props);
            if (updateBuildingGraphic || hasBuildingDataChanged) {
                _this.updateBuildingGraphic(props);
            }
            if (updateCalloutGraphic || hasBuildingDataChanged) {
                _this.updateCalloutGraphic(props);
            }
            _this.interactive = true;
            _this.interactiveChildren = true;
        };
        // Click listener will listen for clicks on this object
        // Not the children
        _this.interactive = true;
        _this.interactiveChildren = false;
        _this.cursor = IdCursor.Pointer;
        _this.tickSubscription$ = utilTick.second$.subscribe(_this.onTick);
        return _this;
    }
    Object.defineProperty(IsoBuilding.prototype, "buildingId", {
        get: function () {
            return this.props.building.proto.id;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(IsoBuilding.prototype, "volume", {
        get: function () {
            return this.props.getBuildingIsoVolume(this.props.building, this.display);
        },
        enumerable: true,
        configurable: true
    });
    IsoBuilding.OutlineFilter = new OutlineFilter(2, 0xffffff);
    return IsoBuilding;
}(Container));
export { IsoBuilding };



//////////////////
// WEBPACK FOOTER
// ./src/features/player-base/buildings/IsoBuilding.ts
// module id = 2209
// module chunks = 3